import {noop} from '@benchling/js-utils';
import PropTypes from 'prop-types';
import React from 'react';
import {connect} from 'react-redux';
import _ from 'underscore';

import {mapReviewCriteriaGroupsToProps} from 'notebook/reviews/reduceReviewCriteria';
import {
  ConfigSavingState,
  REVIEW_CRITERIA_GROUP_SHAPE,
  ReviewCriteriaGroup,
  SavedReviewCriteriaGroup,
} from 'notebook/reviews/ReviewCriteriaConstants';
import ReviewCriteriaGroupConfig from 'notebook/reviews/ReviewCriteriaGroupConfig';
import {
  addCriterion,
  addGroup,
  cancelEdit,
  changeCriterion,
  changeName,
  createReviewCriteriaGroup,
  deleteCriterion,
  deleteReviewCriteriaGroup,
  fetchReviewCriteria,
  newGroupScrolledIntoView,
  startEdit,
  updateReviewCriteriaGroup,
} from 'notebook/reviews/ReviewCriteriaReduxActions';
import ReviewLabelText from 'notebook/reviews/ReviewLabelText';
import {BenchlingState} from 'redux-stores/reduceApp';
import {BenchlingDispatch} from 'redux-stores/Store';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import AddIcon from 'ui-library/icons/AddIcon';

interface ConnectedProps {
  pendingScrollIntoView: boolean;
  reviewCriteriaGroups: Array<ReviewCriteriaGroup> | null;
  savingStateByGroupId: {[groupId: string]: ConfigSavingState | undefined} | null;
  newGroup: ReviewCriteriaGroup | null;
  newGroupSavingState: ConfigSavingState | null | undefined;
  writable: boolean;
}

export function mapStateToProps(
  state: BenchlingState,
  {linkedItemId, writable}: BaseConfigureReviewCriteriaSectionProps
): ConnectedProps {
  const {reviewCriteriaGroupIdsByLinkedItemId} = state.models.reviews;
  const {
    deletingGroupIds,
    pendingScrollIntoViewByLinkedItemId,
    newGroupSavingStatesByLinkedItemId,
    newGroupsByLinkedItemId,
    reviewCriteriaGroupEditsById,
    reviewCriteriaGroupSavingStatesById,
  } = state.ui.reviewCriteriaConfig;

  let groupIds = reviewCriteriaGroupIdsByLinkedItemId[linkedItemId];
  if (!groupIds) {
    return {
      pendingScrollIntoView: false,
      reviewCriteriaGroups: null,
      savingStateByGroupId: null,
      newGroup: null,
      newGroupSavingState: null,
      writable,
    };
  }
  // Don't show groups that are being deleted
  groupIds = _.difference(groupIds, deletingGroupIds);

  const groupProps = mapReviewCriteriaGroupsToProps(state, linkedItemId)!;
  return {
    pendingScrollIntoView: pendingScrollIntoViewByLinkedItemId[linkedItemId] || false,
    reviewCriteriaGroups: groupIds.map(
      (groupId, i) => reviewCriteriaGroupEditsById[groupId] || groupProps[i]
    ),
    savingStateByGroupId: _.object(
      groupIds.map((groupId) => [
        groupId,
        reviewCriteriaGroupSavingStatesById[groupId] || ConfigSavingState.NOT_EDITING,
      ])
    ),
    newGroup: newGroupsByLinkedItemId[linkedItemId],
    newGroupSavingState: newGroupSavingStatesByLinkedItemId[linkedItemId],
    writable,
  };
}

interface DispatchProps {
  onAddCriterion: (groupId: string) => void;
  onAddGroup: () => void;
  onCancelEdit: (groupId: string) => void;
  onChangeCriterion: (groupId: string, rowIndex: number, text: string) => void;
  onChangeGroupName: (groupId: string, name: string) => void;
  onCreateReviewCriteriaGroup: (reviewCriteriaGroup: ReviewCriteriaGroup) => void;
  onDeleteCriterion: (groupId: string, rowIndex: number) => void;
  onDeleteReviewCriteriaGroup: (groupId: string) => void;
  onFetchReviewCriteria: () => void;
  onScrollIntoView: () => void;
  onStartEdit: (reviewCriteriaGroup: SavedReviewCriteriaGroup) => void;
  onUpdateReviewCriteriaGroup: (reviewCriteriaGroup: SavedReviewCriteriaGroup) => void;
}

export function mapDispatchToProps(
  dispatch: BenchlingDispatch,
  {linkedItemId}: BaseConfigureReviewCriteriaSectionProps
): DispatchProps {
  return {
    onAddCriterion(groupId: string): void {
      dispatch(addCriterion(linkedItemId, groupId));
    },
    onAddGroup(): void {
      dispatch(addGroup(linkedItemId));
    },
    onCancelEdit(groupId: string): void {
      dispatch(cancelEdit(linkedItemId, groupId));
    },
    onChangeCriterion(groupId: string, rowIndex: number, text: string): void {
      dispatch(changeCriterion(linkedItemId, groupId, rowIndex, text));
    },
    onChangeGroupName(groupId: string, name: string): void {
      dispatch(changeName(linkedItemId, groupId, name));
    },
    onCreateReviewCriteriaGroup(reviewCriteriaGroup: ReviewCriteriaGroup): void {
      dispatch(createReviewCriteriaGroup(linkedItemId, reviewCriteriaGroup));
    },
    onDeleteCriterion(groupId: string, rowIndex: number): void {
      dispatch(deleteCriterion(linkedItemId, groupId, rowIndex));
    },
    onDeleteReviewCriteriaGroup(groupId: string): void {
      dispatch(deleteReviewCriteriaGroup(linkedItemId, groupId));
    },
    onFetchReviewCriteria(): void {
      dispatch(fetchReviewCriteria(linkedItemId));
    },
    onScrollIntoView(): void {
      dispatch(newGroupScrolledIntoView(linkedItemId));
    },
    onStartEdit(reviewCriteriaGroup: SavedReviewCriteriaGroup): void {
      dispatch(startEdit(linkedItemId, reviewCriteriaGroup.id, reviewCriteriaGroup));
    },
    onUpdateReviewCriteriaGroup(reviewCriteriaGroup: SavedReviewCriteriaGroup): void {
      dispatch(updateReviewCriteriaGroup(linkedItemId, reviewCriteriaGroup));
    },
  };
}

interface BaseConfigureReviewCriteriaSectionProps {
  linkedItemId: string;
  writable: boolean;
}

type ConfigureReviewCriteriaSectionProps = BaseConfigureReviewCriteriaSectionProps &
  ConnectedProps &
  DispatchProps;

export class ConfigureReviewCriteriaSection extends React.Component<ConfigureReviewCriteriaSectionProps> {
  static propTypes = {
    pendingScrollIntoView: PropTypes.bool.isRequired,
    reviewCriteriaGroups: PropTypes.arrayOf(REVIEW_CRITERIA_GROUP_SHAPE.isRequired),
    // eslint-disable-next-line no-restricted-syntax
    savingStateByGroupId: PropTypes.objectOf(PropTypes.oneOf(_.values(ConfigSavingState)).isRequired),
    newGroup: REVIEW_CRITERIA_GROUP_SHAPE,
    // eslint-disable-next-line no-restricted-syntax
    newGroupSavingState: PropTypes.oneOf(_.values(ConfigSavingState)),
    writable: PropTypes.bool.isRequired,

    onAddCriterion: PropTypes.func.isRequired,
    onAddGroup: PropTypes.func.isRequired,
    onCancelEdit: PropTypes.func.isRequired,
    onChangeCriterion: PropTypes.func.isRequired,
    onCreateReviewCriteriaGroup: PropTypes.func.isRequired,
    onDeleteCriterion: PropTypes.func.isRequired,
    onChangeGroupName: PropTypes.func.isRequired,
    onDeleteReviewCriteriaGroup: PropTypes.func.isRequired,
    onFetchReviewCriteria: PropTypes.func.isRequired,
    onScrollIntoView: PropTypes.func.isRequired,
    onStartEdit: PropTypes.func.isRequired,
    onUpdateReviewCriteriaGroup: PropTypes.func.isRequired,
  };

  override componentDidMount(): void {
    this.props.onFetchReviewCriteria();
  }

  override render(): JSX.Element {
    return (
      <Row>
        <Col sm={12}>
          <ReviewLabelText>Review Criteria Groups</ReviewLabelText>
          {this.props.writable && <AddIcon onClick={this.props.onAddGroup} />}
          <div className="configureReviewCriteriaSection">
            {(() => {
              if (!this.props.reviewCriteriaGroups) {
                return (
                  <div className="text-center">
                    <HorizontalThrobber />
                  </div>
                );
              }
              return (
                <div>
                  {this.props.reviewCriteriaGroups.map((reviewCriteriaGroup) => (
                    <ReviewCriteriaGroupConfig
                      key={reviewCriteriaGroup.id}
                      reviewCriteriaGroup={reviewCriteriaGroup}
                      savingState={
                        reviewCriteriaGroup.id && this.props.savingStateByGroupId
                          ? this.props.savingStateByGroupId[reviewCriteriaGroup.id]
                          : undefined
                      }
                      writable={this.props.writable}
                      onAddCriterion={this.props.onAddCriterion}
                      onCancelEdit={this.props.onCancelEdit}
                      onChangeGroupName={this.props.onChangeGroupName}
                      onChangeCriterion={this.props.onChangeCriterion}
                      onDeleteCriterion={this.props.onDeleteCriterion}
                      onDeleteReviewCriteriaGroup={this.props.onDeleteReviewCriteriaGroup}
                      onSaveReviewCriteriaGroup={this.props.onUpdateReviewCriteriaGroup}
                      onStartEdit={this.props.onStartEdit}
                    />
                  ))}
                  {this.props.writable && this.props.newGroup && (
                    <ReviewCriteriaGroupConfig
                      pendingScrollIntoView={this.props.pendingScrollIntoView}
                      reviewCriteriaGroup={this.props.newGroup}
                      savingState={this.props.newGroupSavingState}
                      writable={true}
                      onAddCriterion={this.props.onAddCriterion}
                      onCancelEdit={this.props.onCancelEdit}
                      onChangeGroupName={this.props.onChangeGroupName}
                      onChangeCriterion={this.props.onChangeCriterion}
                      onDeleteCriterion={this.props.onDeleteCriterion}
                      onSaveReviewCriteriaGroup={this.props.onCreateReviewCriteriaGroup}
                      onScrollIntoView={this.props.onScrollIntoView}
                      // The new group is never in the NOT_EDITING state, so these should never be called
                      onDeleteReviewCriteriaGroup={noop}
                      onStartEdit={noop}
                    />
                  )}
                </div>
              );
            })()}
          </div>
        </Col>
      </Row>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(ConfigureReviewCriteriaSection);
